import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { getJson, mutateJson } from '../api/client';
import { Field, Input, Select } from '../components/form';
import { Alert, Badge, Button, Card, EmptyState, Spinner } from '../components/ui';
import { brl, dataBr, mesAno } from '../lib/format';
import styles from './screens.module.css';

interface Competencia {
  id: number;
  ano: number;
  mes: number;
}
type Status = 'pago' | 'divergente' | 'atrasado' | 'aberto';
interface Fatura {
  id: number;
  unidadeNumero: string | null;
  subtotal: number;
  vencimento: string | null;
  dataPagamento: string | null;
  status: Status;
}
interface ResultadoPagamento {
  error?: string;
  dias?: number;
  multa?: number;
  juros?: number;
}
interface ResultadoEnvio {
  error?: string;
  destinatarios?: number;
  enviados?: number;
}

type Carga = { estado: 'carregando' } | { estado: 'erro' } | { estado: 'ok'; faturas: Fatura[] };
type Aviso = { kind: 'success' | 'warning' | 'danger'; texto: string };
type Confirmacao = 'gerar' | 'avisar' | 'lembrar';

const STATUS_ROTULO: Record<Status, string> = {
  pago: 'Pago',
  divergente: 'Divergente',
  atrasado: 'Atrasado',
  aberto: 'Em aberto',
};
const MSG_DATA = 'Informe a data do pagamento.';
const MSG_DATA_FUTURA = 'A data do pagamento não pode ser futura.';
/** Erros da API fora do formulário de pagamento (código → mensagem). */
const ERRO_GERAL: Record<string, string> = {
  tenant_suspended: 'Condomínio suspenso — somente leitura.',
  sem_unidades: 'Cadastre ao menos uma unidade ativa.',
  pagamentos_registrados: 'Já há pagamento registrado: não dá para gerar as faturas de novo.',
  demonstrativo_not_found: 'Fatura não encontrada.',
  competencia_not_found: 'Competência não encontrada.',
};
const ERRO_GERAL_PADRAO = 'Não foi possível concluir. Tente novamente.';
/** Envios por e-mail: rota, texto de confirmação, rótulo do resultado e aviso sem destinatários. */
const ENVIOS: Record<
  'avisar' | 'lembrar',
  { rota: string; pergunta: string; resultado: string; vazio: string }
> = {
  avisar: {
    rota: 'notificar',
    pergunta: 'Enviar o aviso de fatura publicada a todos os moradores com e-mail?',
    resultado: 'E-mails enviados',
    vazio: 'Nenhum morador com e-mail para receber este envio.',
  },
  lembrar: {
    rota: 'notificar-inadimplencia',
    pergunta:
      'Enviar o lembrete às unidades que ainda não pagaram (em aberto, atrasadas ou com pagamento divergente)?',
    resultado: 'Lembretes enviados',
    vazio: 'Nenhuma unidade pendente com e-mail para receber o lembrete.',
  },
};

/**
 * Faturas da competência (RF-A08, RF-A11, RF-B07): o síndico gera, acompanha o status por
 * unidade, avisa por e-mail e registra pagamento manual; o contábil só lê. `editavel` só para
 * o síndico de condomínio ativo (as escritas são `requireRole('sindico')`; suspenso é somente
 * leitura, #127). Valores, status, atraso, multa e juros vêm sempre do servidor.
 */
export function FaturasDoMes({ editavel }: { editavel: boolean }) {
  const [comps, setComps] = useState<Competencia[] | 'erro' | null>(null);
  const [sel, setSel] = useState('');
  const [carga, setCarga] = useState<Carga>({ estado: 'carregando' });
  const [aviso, setAviso] = useState<Aviso | null>(null);
  const [confirmando, setConfirmando] = useState<Confirmacao | null>(null);
  // Só o id: a fatura em pagamento vem sempre da lista atual.
  const [pagandoId, setPagandoId] = useState<number | null>(null);
  const [ocupado, setOcupado] = useState(false);
  // Competência selecionada no momento: respostas de outra (troca rápida) são descartadas.
  const selAtual = useRef(sel);
  selAtual.current = sel;

  useEffect(() => {
    void getJson<{ competencias: Competencia[] }>('/api/competencias').then((d) => {
      if (!d) {
        setComps('erro');
        return;
      }
      setComps(d.competencias);
      if (d.competencias[0]) setSel(String(d.competencias[0].id));
    });
  }, []);

  const carregar = useCallback(async (competencia: string) => {
    const dados = await getJson<{ demonstrativos: Fatura[] }>(
      `/api/competencias/${encodeURIComponent(competencia)}/demonstrativos`,
    );
    if (selAtual.current !== competencia) return;
    setCarga(dados ? { estado: 'ok', faturas: dados.demonstrativos } : { estado: 'erro' });
  }, []);

  useEffect(() => {
    if (!sel) return;
    setCarga({ estado: 'carregando' });
    setConfirmando(null);
    setPagandoId(null);
    void carregar(sel);
  }, [sel, carregar]);

  function falha(codigo?: string): Aviso {
    return { kind: 'danger', texto: (codigo && ERRO_GERAL[codigo]) || ERRO_GERAL_PADRAO };
  }

  async function gerar() {
    if (ocupado) return;
    const origem = sel;
    setOcupado(true);
    setAviso(null);
    const res = await mutateJson<{ error?: string }>(
      `/api/competencias/${encodeURIComponent(origem)}/demonstrativos`,
      {},
    );
    setOcupado(false);
    if (selAtual.current !== origem) return;
    setConfirmando(null);
    if (!res.ok) {
      setAviso(falha(res.data?.error));
      return;
    }
    setAviso({ kind: 'success', texto: 'Faturas geradas.' });
    await carregar(origem);
  }

  async function enviar(tipo: 'avisar' | 'lembrar') {
    if (ocupado) return;
    const origem = sel;
    const envio = ENVIOS[tipo];
    setOcupado(true);
    setAviso(null);
    const res = await mutateJson<ResultadoEnvio>(
      `/api/competencias/${encodeURIComponent(origem)}/${envio.rota}`,
      {},
    );
    setOcupado(false);
    if (selAtual.current !== origem) return;
    setConfirmando(null);
    if (!res.ok) {
      setAviso(falha(res.data?.error));
      return;
    }
    const total = res.data?.destinatarios ?? 0;
    const enviados = res.data?.enviados ?? 0;
    if (total === 0) {
      setAviso({ kind: 'warning', texto: envio.vazio });
      return;
    }
    setAviso({
      kind: enviados === total ? 'success' : 'warning',
      texto: `${envio.resultado}: ${enviados} de ${total}.`,
    });
  }

  async function aoPagar(numero: string | null, resultado: ResultadoPagamento) {
    if (selAtual.current !== sel) return;
    setPagandoId(null);
    const dias = resultado.dias ?? 0;
    setAviso(
      dias > 0
        ? {
            kind: 'warning',
            texto: `Atraso de ${dias} ${dias === 1 ? 'dia' : 'dias'}: multa de ${brl(resultado.multa ?? 0)} e juros de ${brl(resultado.juros ?? 0)}, cobrados na fatura do mês seguinte.`,
          }
        : { kind: 'success', texto: `Pagamento da unidade ${numero ?? ''} registrado.` },
    );
    await carregar(sel);
  }

  async function aoFalharPagamento(codigo?: string) {
    if (selAtual.current !== sel) return;
    setAviso(falha(codigo));
    if (codigo !== 'demonstrativo_not_found') return;
    setPagandoId(null);
    await carregar(sel);
  }

  if (comps === null) return <Spinner />;
  if (comps === 'erro') return <Alert kind="danger">Não foi possível carregar as faturas.</Alert>;
  if (comps.length === 0) return <EmptyState>Nenhuma competência aberta.</EmptyState>;

  const faturas = carga.estado === 'ok' ? carga.faturas : null;
  const pagando = faturas?.find((f) => f.id === pagandoId) ?? null;

  return (
    <div className={styles.stack}>
      <Field label="Competência">
        {(id) => (
          <Select
            id={id}
            value={sel}
            onChange={(e) => {
              setAviso(null);
              setSel(e.target.value);
            }}
            style={{ maxWidth: '12rem' }}
          >
            {comps.map((c) => (
              <option key={c.id} value={c.id}>
                {mesAno(c)}
              </option>
            ))}
          </Select>
        )}
      </Field>

      {aviso ? <Alert kind={aviso.kind}>{aviso.texto}</Alert> : null}
      {carga.estado === 'carregando' ? <Spinner /> : null}
      {carga.estado === 'erro' ? (
        <Alert kind="danger">Não foi possível carregar as faturas.</Alert>
      ) : null}

      {faturas && faturas.length === 0 ? (
        editavel ? (
          <Card title="Gerar faturas">
            <p>
              Calcula a fatura de cada unidade ativa com a previsão do mês, os fundos e o gás
              calculado.
            </p>
            <Button disabled={ocupado} onClick={() => void gerar()}>
              Gerar faturas
            </Button>
          </Card>
        ) : (
          <EmptyState>Faturas ainda não geradas neste mês.</EmptyState>
        )
      ) : null}

      {editavel && faturas && faturas.length > 0 ? (
        <Card title="Ações do mês">
          {confirmando === 'gerar' ? (
            <div className={styles.row}>
              <span>
                Gerar de novo recalcula todas as faturas com a previsão e o gás de agora. Só é
                possível enquanto nenhuma foi paga.
              </span>
              <Button disabled={ocupado} onClick={() => void gerar()}>
                Confirmar geração
              </Button>
              <Button variant="ghost" onClick={() => setConfirmando(null)}>
                Cancelar
              </Button>
            </div>
          ) : confirmando ? (
            <div className={styles.row}>
              <span>{ENVIOS[confirmando].pergunta}</span>
              <Button disabled={ocupado} onClick={() => void enviar(confirmando)}>
                Confirmar envio
              </Button>
              <Button variant="ghost" onClick={() => setConfirmando(null)}>
                Cancelar envio
              </Button>
            </div>
          ) : (
            <div className={styles.row}>
              <Button onClick={() => setConfirmando('avisar')}>Avisar que as faturas saíram</Button>
              <Button variant="secondary" onClick={() => setConfirmando('lembrar')}>
                Lembrar quem não pagou
              </Button>
              <Button variant="ghost" onClick={() => setConfirmando('gerar')}>
                Gerar novamente
              </Button>
            </div>
          )}
        </Card>
      ) : null}

      {editavel && pagando ? (
        <PagamentoForm
          key={pagando.id}
          fatura={pagando}
          onPago={aoPagar}
          onCancelar={() => setPagandoId(null)}
          onErroGeral={aoFalharPagamento}
        />
      ) : null}

      {faturas && faturas.length > 0 ? (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Unidade</th>
                <th>Vencimento</th>
                <th className={styles.num}>Valor</th>
                <th>Situação</th>
                {editavel ? <th /> : null}
              </tr>
            </thead>
            <tbody>
              {faturas.map((f) => (
                <tr key={f.id}>
                  <th scope="row">{f.unidadeNumero ?? '—'}</th>
                  <td>{f.vencimento ? dataBr(f.vencimento) : '—'}</td>
                  <td className={styles.num}>{brl(f.subtotal)}</td>
                  <td>
                    <div className={styles.row}>
                      <Badge tone={TOM[f.status]}>{STATUS_ROTULO[f.status]}</Badge>
                      {f.status === 'pago' && f.dataPagamento ? (
                        <span className="muted">Pago em {dataBr(f.dataPagamento)}</span>
                      ) : null}
                    </div>
                  </td>
                  {editavel ? (
                    <td>
                      {f.status !== 'pago' ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          aria-label={`Registrar pagamento da unidade ${f.unidadeNumero ?? ''}`}
                          onClick={() => {
                            setAviso(null);
                            setPagandoId(f.id);
                          }}
                        >
                          Registrar pagamento
                        </Button>
                      ) : null}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

const TOM: Record<Status, 'success' | 'warning' | undefined> = {
  pago: 'success',
  divergente: 'warning',
  atrasado: 'warning',
  aberto: undefined,
};

/** Pagamento manual de uma fatura: data do pagamento; atraso, multa e juros vêm do servidor. */
function PagamentoForm({
  fatura,
  onPago,
  onCancelar,
  onErroGeral,
}: {
  fatura: Fatura;
  onPago: (numero: string | null, resultado: ResultadoPagamento) => Promise<void>;
  onCancelar: () => void;
  /** Erro da API fora do campo (código; ausente = falha de rede). */
  onErroGeral: (codigo?: string) => Promise<void>;
}) {
  // Hoje no fuso do navegador (AAAA-MM-DD): não se registra pagamento no futuro.
  const hoje = new Date(Date.now() - new Date().getTimezoneOffset() * 60_000)
    .toISOString()
    .slice(0, 10);
  const [data, setData] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    if (!data) {
      setErro(MSG_DATA);
      return;
    }
    if (data > hoje) {
      setErro(MSG_DATA_FUTURA);
      return;
    }
    setErro(null);
    setEnviando(true);
    const res = await mutateJson<ResultadoPagamento>(
      `/api/demonstrativos/${encodeURIComponent(fatura.id)}/pagamento`,
      { dataPagamento: data },
    );
    setEnviando(false);
    if (res.ok) {
      await onPago(fatura.unidadeNumero, res.data ?? {});
      return;
    }
    if (res.data?.error === 'invalid_data') setErro(MSG_DATA);
    else await onErroGeral(res.data?.error);
  }

  return (
    <Card title={`Registrar pagamento da unidade ${fatura.unidadeNumero ?? ''}`}>
      <form onSubmit={enviar} noValidate>
        <div className={styles.row}>
          <Field label="Data do pagamento" error={erro}>
            {(id) => (
              <Input
                id={id}
                type="date"
                value={data}
                max={hoje}
                onChange={(e) => setData(e.target.value)}
              />
            )}
          </Field>
        </div>
        <div className={styles.row}>
          <Button type="submit" disabled={enviando}>
            Salvar pagamento
          </Button>
          <Button variant="ghost" onClick={onCancelar}>
            Cancelar
          </Button>
        </div>
      </form>
    </Card>
  );
}
